import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import {
  createStocktake,
  fetchStocktakeDetail,
  updateStocktake,
} from '#/api/imsMgmt/receipts/stocktake';
import { ROUTES } from '#/config/routes';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import { Button } from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import PageShell from '#/modules/infra/layout/PageShell';
import StocktakeMainInfoCard from '#/modules/imsMgmt/receipts/stocktake/components/StocktakeMainInfoCard';
import StocktakeScanLinesEditor from '#/modules/imsMgmt/receipts/stocktake/components/StocktakeScanLinesEditor';
import StocktakeScanNotice from '#/modules/imsMgmt/receipts/stocktake/components/StocktakeScanNotice';
import {
  getStocktakeApiErrorMessage,
  isStocktakeVersionConflict,
} from '#/modules/imsMgmt/receipts/stocktake/helper/apiError';
import { isBlankCountedQty } from '#/modules/imsMgmt/receipts/stocktake/helper/isBlankCountedQty';
import {
  mapStocktakeDetailToFormState,
} from '#/modules/imsMgmt/receipts/stocktake/helper/mapStocktakeDetailToForm';
import {
  sortStocktakeLinesByStockNo,
} from '#/modules/imsMgmt/receipts/stocktake/helper/sortStocktakeLinesByStockNo';
import useInventoryStockScan from '#/modules/imsMgmt/receipts/stocktake/hooks/useInventoryStockScan';
import {
  stocktakeDetailQueryKey,
  stocktakeListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

function parseCountedQtyInput(raw) {
  if (raw === '' || raw == null) {
    return '';
  }
  const qty = Number(raw);
  return Number.isFinite(qty) ? qty : '';
}

function resolveCountedQtyError(raw) {
  if (isBlankCountedQty(raw)) {
    return 'blank';
  }
  const qty = Number(raw);
  if (!Number.isFinite(qty) || qty < 0) {
    return 'negative';
  }
  return null;
}

function collectLineErrors(lines) {
  const next = {};
  (lines ?? []).forEach((line, index) => {
    const qtyReason = resolveCountedQtyError(line?.countedQty);
    if (qtyReason) {
      next[index] = qtyReason;
    }
  });
  return next;
}

function formatLineErrorSummary(invalidByIndex) {
  const entries = Object.entries(invalidByIndex ?? {})
    .map(([index, reason]) => ({ index: Number(index), reason }))
    .filter((item) => Number.isInteger(item.index))
    .sort((left, right) => left.index - right.index);

  if (entries.length === 0) {
    return '';
  }

  if (entries.length === 1) {
    const item = entries[0];
    const label = `#${item.index + 1}`;
    if (item.reason === 'negative') {
      return `${label} 盤點數量不可小於 0`;
    }
    return `${label} 盤點數量不可空白`;
  }

  const labels = entries.map((item) => `#${item.index + 1}`).join('、');
  return `${labels} …`;
}

const StocktakeScanForm = ({
  id = null,
  pageTitle,
  detail = null,
  initialRemark = '',
  initialLines = [],
  initialUpdatedAt = null,
  onVersionConflict,
}) => {
  const updatedAtRef = useRef(initialUpdatedAt);

  const [lines, setLines] = useState(() => sortStocktakeLinesByStockNo(initialLines));
  const [remark, setRemark] = useState(initialRemark);
  const [invalidByIndex, setInvalidByIndex] = useState({});

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createStocktake,
    updateFn: updateStocktake,
    buildPayload: (values) => ({
      ...values,
      updatedAt: id ? updatedAtRef.current : null,
    }),
    listQueryKeyPrefix: stocktakeListQueryKeyPrefix,
    detailQueryKey: stocktakeDetailQueryKey,
    getDetailPath: ROUTES.stocktakeDetail,
    getErrorMessage: (error) => getStocktakeApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isStocktakeVersionConflict,
    onVersionConflict,
  });

  const {
    scanCode,
    setScanCode,
    onScanEnter,
    isScanning,
    inputRef,
    scanNotice,
    highlightInvStockId,
    ConfirmDialogHost,
    AmbiguousDialogHost,
    CoverOverlayHost,
  } = useInventoryStockScan({
    lines,
    onAppendLine: (line) => {
      setLines((prev) => sortStocktakeLinesByStockNo([...prev, line]));
      setInvalidByIndex({});
    },
  });

  useEffect(() => {
    inputRef.current?.focus?.();
  }, [inputRef]);

  const errorSummary = useMemo(
    () => formatLineErrorSummary(invalidByIndex),
    [invalidByIndex],
  );

  const isSubmitting = isPending;

  function handleCountedQtyChange(index, raw) {
    const nextValue = raw === '' ? '' : raw;
    setLines((prev) => prev.map((line, lineIndex) => (
      lineIndex === index
        ? { ...line, countedQty: nextValue }
        : line
    )));
    const reason = resolveCountedQtyError(nextValue);
    setInvalidByIndex((prev) => {
      const next = { ...prev };
      if (reason) {
        next[index] = reason;
      } else if (next[index] === 'blank' || next[index] === 'negative') {
        delete next[index];
      }
      return next;
    });
  }

  function handleCountedQtyBlur(index, raw) {
    if (isBlankCountedQty(raw)) {
      setLines((prev) => prev.map((line, lineIndex) => {
        if (lineIndex !== index) {
          return line;
        }
        return { ...line, countedQty: line.systemQty ?? 0 };
      }));
      toast.error('盤點數量不可空白');
      setInvalidByIndex((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      return;
    }

    const qty = parseCountedQtyInput(raw);
    const nextValue = qty === '' ? '' : qty;
    setLines((prev) => prev.map((line, lineIndex) => {
      if (lineIndex !== index) {
        return line;
      }
      if (nextValue === '') {
        return { ...line, countedQty: line.systemQty ?? 0 };
      }
      return { ...line, countedQty: nextValue };
    }));
    const reason = resolveCountedQtyError(nextValue === '' ? raw : nextValue);
    setInvalidByIndex((prev) => {
      const next = { ...prev };
      if (reason) {
        next[index] = reason;
      } else {
        delete next[index];
      }
      return next;
    });
  }

  function handleRemove(index) {
    setLines((prev) => prev.filter((_, lineIndex) => lineIndex !== index));
    setInvalidByIndex((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, reason]) => {
        const current = Number(key);
        if (current === index) {
          return;
        }
        next[current > index ? current - 1 : current] = reason;
      });
      return next;
    });
  }

  function handleSubmitClick() {
    const sortedLines = sortStocktakeLinesByStockNo(lines);
    setLines(sortedLines);

    const nextErrors = collectLineErrors(sortedLines);
    setInvalidByIndex(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    save({
      remark: remark.trim() || null,
      lines: sortedLines,
    });
  }

  return (
    <>
      <CoverOverlayHost />
      {ConfirmDialogHost}
      <AmbiguousDialogHost />

      <div className="mx-auto w-full max-w-6xl">
        <div className="create_edit_page__header">
          <h1 className="page_title mb-0!">
            <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
          </h1>
          <Button type="button" variant="outline" asChild>
            <Link to={ROUTES.stocktake}>返回列表</Link>
          </Button>
        </div>

        {isEdit && detail ? (
          <div className="mb-4">
            <StocktakeMainInfoCard detail={detail} />
          </div>
        ) : null}

        <StocktakeScanNotice notice={scanNotice} />

        <form
          className="sheet create_edit create_edit_form create_edit_form--page-scroll"
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmitClick();
          }}
          noValidate
        >
          <div className="create_edit_form__body divide-y divide-gray-500">
            <section className="pb-6">
              <div className="form-block">
                <h2 className="form-block__title">掃描庫存</h2>
                <p className="form-block__description">
                  以掃碼槍或手動輸入完整庫存編號後按 Enter 加入清單。
                  查無庫存請抄下庫編後另行處理（不可加入清單）。
                </p>
              </div>
              <div className="space-y-2">
                <FloatingInput
                  id="stocktake-scan-code"
                  label="庫存編號"
                  value={scanCode}
                  readOnly={isScanning || isSubmitting}
                  register={() => ({
                    ref: inputRef,
                    value: scanCode,
                    onChange: (event) => setScanCode(event.target.value),
                  })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      onScanEnter();
                    }
                  }}
                  containerClassName="max-w-md"
                  autoComplete="off"
                />
                <FloatingTextarea
                  id="stocktake-remark"
                  label="備註"
                  value={remark}
                  onChange={(event) => setRemark(event.target.value)}
                  rows={2}
                  containerClassName="max-w-xl"
                />
              </div>
            </section>

            <section className="pt-6">
              <div className="form-block">
                <h2 className="form-block__title">
                  已輸入庫存
                  {lines.length > 0 ? `（${lines.length}）` : ''}
                </h2>
              </div>
              {errorSummary ? (
                <p className="mb-3 text-sm text-red-500">{errorSummary}</p>
              ) : null}
              <StocktakeScanLinesEditor
                lines={lines}
                onCountedQtyChange={handleCountedQtyChange}
                onCountedQtyBlur={handleCountedQtyBlur}
                onRemove={handleRemove}
                invalidByIndex={invalidByIndex}
                highlightInvStockId={highlightInvStockId}
              />
            </section>
          </div>

          <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.stocktake}>取消</Link>
            </Button>
            <Button
              type="submit"
              disabled={lines.length === 0 || isScanning || isSubmitting}
            >
              {isSubmitting ? '送出中…' : (isEdit ? '儲存盤點' : '送出盤點')}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

StocktakeScanForm.propTypes = {
  id: PropTypes.string,
  pageTitle: PropTypes.string.isRequired,
  detail: PropTypes.object,
  initialRemark: PropTypes.string,
  initialLines: PropTypes.arrayOf(PropTypes.object),
  initialUpdatedAt: PropTypes.string,
  onVersionConflict: PropTypes.func,
};

const CreateEditStocktakePage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);

  const detailQuery = useQuery({
    queryKey: stocktakeDetailQueryKey(id),
    queryFn: () => fetchStocktakeDetail(id),
    enabled: isEdit,
  });

  const detail = detailQuery.data?.data;
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const pageTitle = isEdit ? '編輯盤點單' : '新增盤點單';

  const formSeed = useMemo(() => {
    if (!isEdit || !detail) {
      return null;
    }
    return mapStocktakeDetailToFormState(detail);
  }, [isEdit, detail]);

  if (isPageLoading) {
    return (
      <PageShell variant="form">
        <div className="layout-shell__body">
          <p className="text-sm text-slate-500">載入中…</p>
        </div>
      </PageShell>
    );
  }

  if (isDetailNotFound) {
    return (
      <PageShell variant="form">
        <div className="layout-shell__body">
          <p className="text-sm text-destructive">
            {getStocktakeApiErrorMessage(detailQuery.error, '載入盤點單失敗')}
          </p>
          <Button type="button" variant="outline" asChild className="mt-3">
            <Link to={ROUTES.stocktake}>返回列表</Link>
          </Button>
        </div>
      </PageShell>
    );
  }

  if (isEdit && !formSeed) {
    return (
      <PageShell variant="form">
        <div className="layout-shell__body">
          <p className="text-sm text-slate-500">載入中…</p>
        </div>
      </PageShell>
    );
  }

  const formKey = isEdit
    ? `${id}-${formSeed.updatedAt ?? 'na'}`
    : 'create';

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <StocktakeScanForm
          key={formKey}
          id={id ?? null}
          pageTitle={pageTitle}
          detail={isEdit ? detail : null}
          initialRemark={formSeed?.remark ?? ''}
          initialLines={formSeed?.lines ?? []}
          initialUpdatedAt={formSeed?.updatedAt ?? null}
          onVersionConflict={isEdit ? () => { detailQuery.refetch(); } : undefined}
        />
      </div>
    </PageShell>
  );
};

export default CreateEditStocktakePage;
